import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";

/**
 * Central hook that fetches and normalises every proactive alert displayed
 * in the Dashboard. Each `provider` returns either a normalised alert
 * object or `null` (no alert to show).
 *
 * Alert schema:
 *   {
 *     id: string (unique & stable),
 *     severity: 'critical' | 'warning' | 'info',
 *     title: string,
 *     description: string,
 *     icon: lucide component,
 *     tone: tailwind class bundle { bg, border, iconBg, title, text },
 *     actions: [{ label, onClick OR href, primary?, testid? }],
 *     createdAt: number (ms, for tie-breakers)
 *   }
 *
 * `useDashboardAlerts` returns `{alerts, loading, refresh}` with alerts
 * already sorted (critical → warning → info) and dismissed entries filtered.
 */

const DISMISS_TTL_HOURS = 24;
const dismissKey = (rid, alertId) => `dashboard-alert-dismissed::${rid}::${alertId}`;

export function isDismissed(restaurantId, alertId) {
  try {
    const raw = localStorage.getItem(dismissKey(restaurantId, alertId));
    if (!raw) return false;
    const untilMs = Number(raw);
    if (!untilMs || Date.now() > untilMs) {
      localStorage.removeItem(dismissKey(restaurantId, alertId));
      return false;
    }
    return true;
  } catch { return false; }
}

export function markDismissed(restaurantId, alertId) {
  try {
    const until = Date.now() + DISMISS_TTL_HOURS * 3600 * 1000;
    localStorage.setItem(dismissKey(restaurantId, alertId), String(until));
  } catch { /* no-op */ }
}

const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

const TONES = {
  critical: {
    bg: "from-red-50 via-rose-50 to-red-100",
    border: "border-red-200",
    iconBg: "bg-red-600",
    title: "text-red-900",
    text: "text-red-900/90",
  },
  warning: {
    bg: "from-amber-50 via-orange-50 to-amber-100",
    border: "border-amber-200",
    iconBg: "bg-amber-600",
    title: "text-amber-900",
    text: "text-amber-900/90",
  },
  info: {
    bg: "from-emerald-50 via-teal-50 to-cyan-50",
    border: "border-emerald-200",
    iconBg: "bg-emerald-600",
    title: "text-emerald-900",
    text: "text-emerald-900/90",
  },
};

// ──────────────────── Individual providers ────────────────────

import {
  AlertTriangle, TrendingDown, PackageX, Zap, Scale, FileText, Sparkles,
} from "lucide-react";

async function _archivedSoonProvider(navigate, restaurantId) {
  try {
    const archived = await api.getArchivedRestaurants();
    if (!Array.isArray(archived) || archived.length === 0) return null;
    const list = archived.filter(r => r.delete_at);
    if (!list.length) return null;
    list.sort((a, b) => new Date(a.delete_at) - new Date(b.delete_at));
    const next = list[0];
    const diffDays = Math.ceil((new Date(next.delete_at) - new Date()) / 86400000);
    if (diffDays >= 7) return null;
    const urgent = diffDays < 1;
    const severity = urgent ? "critical" : "warning";
    const text = diffDays >= 1 ? `${diffDays} día${diffDays === 1 ? "" : "s"}` : "menos de 24 h";
    return {
      id: `archived-soon-${next.restaurant_id}`,
      severity,
      icon: AlertTriangle,
      tone: TONES[severity],
      title: `${next.name} se eliminará pronto`,
      description: `Quedan ${text} para el borrado definitivo. Restáuralo o exporta una copia.`,
      actions: [
        {
          label: "Ver en Archivados",
          href: "/app/settings?tab=restaurants&archived=1",
          primary: true,
          testid: "alert-archived-soon-cta",
        },
      ],
      createdAt: new Date(next.archived_at).getTime() || Date.now(),
    };
  } catch { return null; }
}

async function _varianceProvider(navigate, restaurantId) {
  try {
    const res = await api.request(`/restaurants/${restaurantId}/variance-alerts/history?limit=3`);
    const items = res?.items || [];
    if (!items.length) return null;
    const latest = items[0];
    const count = latest.alerts_count || (latest.alerts || []).length;
    if (!count) return null;
    const maxPct = Math.max(0, ...(latest.alerts || []).map(a => Math.abs(a.delta_pct || 0)));
    if (maxPct < 5) return null;  // low-impact noise
    const severity = maxPct > 15 ? "critical" : "warning";
    const lossEur = Math.round(latest.total_loss_value || 0);
    const topName = (latest.alerts || [])[0]?.name;
    return {
      id: `variance-${latest.history_id || latest.generated_at || "current"}`,
      severity,
      icon: Scale,
      tone: TONES[severity],
      title: `Merma oculta: ${count} producto${count === 1 ? "" : "s"}${lossEur > 0 ? ` · ${lossEur}€` : ""}`,
      description: topName
        ? `Mayor desviación en ${topName} (${maxPct.toFixed(0)}%). Revisa stock real vs ventas.`
        : `Detectamos ${count} ingredientes con pérdida oculta. Revisa stock real vs ventas.`,
      actions: [
        {
          label: "Revisar mermas",
          href: "/app/inventory?tab=variance",
          primary: true,
          testid: "alert-variance-cta",
        },
      ],
      createdAt: new Date(latest.generated_at || Date.now()).getTime(),
    };
  } catch { return null; }
}

async function _savingsProvider(navigate, restaurantId) {
  try {
    const res = await api.getSavingsOpportunity(restaurantId, { days: 90, top: 3 });
    const total = res?.total_monthly_savings || 0;
    if (total < 100) return null;  // only meaningful amounts
    const fmt = new Intl.NumberFormat("es-ES", {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(total);
    return {
      id: `savings-${Math.round(total)}`,
      severity: "info",
      icon: TrendingDown,
      tone: TONES.info,
      title: `Oportunidad: ahorra ${fmt}/mes`,
      description: `${res.total_opportunities} productos donde pagas por encima de la media del mercado. Revisa proveedores alternativos.`,
      actions: [
        {
          label: "Ver proveedores",
          href: "/app/suppliers",
          primary: true,
          testid: "alert-savings-cta",
        },
      ],
      createdAt: Date.now(),
    };
  } catch { return null; }
}

async function _lowStockProvider(navigate, restaurantId) {
  try {
    const res = await api.request(`/restaurants/${restaurantId}/inventory/dashboard`);
    const alerts = res?.alerts || [];
    const low = alerts.find(a => a.type === "low_stock");
    if (!low || !low.count) return null;
    const severity = low.count >= 5 ? "critical" : "warning";
    return {
      id: `low-stock-${low.count}`,
      severity,
      icon: PackageX,
      tone: TONES[severity],
      title: low.title || `${low.count} productos bajo mínimos`,
      description: low.message || "Rellena tu inventario antes de que falten en cocina.",
      actions: [
        {
          label: "Ver inventario",
          href: "/app/inventory",
          primary: true,
          testid: "alert-low-stock-cta",
        },
      ],
      createdAt: Date.now(),
    };
  } catch { return null; }
}


async function _financePendingProvider(navigate, restaurantId) {
  try {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const to = today.toISOString().slice(0, 10);
    const s = await api.getFinanceSummary(restaurantId, { from, to });
    const pending = s?.pending_docs || 0;
    const unpaidCount = s?.unpaid_count || 0;
    const unpaidTotal = s?.unpaid_total || 0;
    if (pending === 0 && unpaidCount === 0) return null;

    if (unpaidCount > 0 && unpaidTotal > 0) {
      const fmt = new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(unpaidTotal);
      return {
        id: `finance-unpaid-${unpaidCount}-${Math.round(unpaidTotal)}`,
        severity: "warning",
        icon: FileText,
        tone: TONES.warning,
        title: `${unpaidCount} factura${unpaidCount === 1 ? "" : "s"} sin pagar (${fmt})`,
        description: "Revisa los vencimientos para evitar sanciones o cortes de suministro.",
        actions: [{ label: "Ver contabilidad", href: "/app/finance?tab=inbox", primary: true, testid: "alert-finance-unpaid-cta" }],
        createdAt: Date.now(),
      };
    }
    return {
      id: `finance-pending-${pending}`,
      severity: "info",
      icon: FileText,
      tone: TONES.info,
      title: `${pending} documento${pending === 1 ? "" : "s"} en la bandeja`,
      description: "La IA ya extrajo los datos — sólo confirma para que entren en el P&L.",
      actions: [{ label: "Revisar", href: "/app/finance?tab=inbox", primary: true, testid: "alert-finance-pending-cta" }],
      createdAt: Date.now(),
    };
  } catch { return null; }
}


async function _lowCreditsProvider(navigate, restaurantId) {
  try {
    const res = await api.request(`/subscription/credits/${restaurantId}`);
    const remaining = Number(res?.credits_remaining || 0);
    const total = Number(res?.credits_total || 0);
    if (total <= 0 || remaining < 0) return null;
    const pct = (remaining / total) * 100;
    if (pct > 20) return null;  // healthy
    const severity = pct <= 5 ? "critical" : "warning";
    return {
      id: `low-credits-${remaining}`,
      severity,
      icon: Zap,
      tone: TONES[severity],
      title: remaining === 0
        ? "Sin créditos IA disponibles"
        : `Te quedan ${remaining} créditos IA`,
      description: remaining === 0
        ? "No podrás usar IA (generación, análisis, OCR…) hasta añadir balance."
        : `Menos del ${Math.round(pct)}% del plan. Recarga para seguir usando IA sin interrupciones.`,
      actions: [
        {
          label: "Añadir balance",
          href: "/app/subscription",
          primary: true,
          testid: "alert-credits-cta",
        },
      ],
      createdAt: Date.now(),
    };
  } catch { return null; }
}

async function _financeInsightProvider(navigate, restaurantId) {
  try {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
    const to = today.toISOString().slice(0, 10);
    const res = await api.getFinanceInsights(restaurantId, { from, to });
    const warnings = (res?.insights || []).filter(i => i.severity === "warning");
    if (!warnings.length) return null;
    // Pick the first (most relevant in the ordered array)
    const w = warnings[0];
    return {
      id: `finance-insight-${w.title.slice(0, 40).replace(/\s+/g, "-").toLowerCase()}`,
      severity: "warning",
      icon: Sparkles,
      tone: TONES.warning,
      title: w.title,
      description: w.body,
      actions: [
        { label: "Ver en Contabilidad", href: "/app/finance", primary: true, testid: "alert-finance-insight-cta" },
      ],
      createdAt: res.generated_at ? new Date(res.generated_at).getTime() : Date.now(),
    };
  } catch { return null; }
}


const PROVIDERS = [
  _archivedSoonProvider,
  _varianceProvider,
  _savingsProvider,
  _lowStockProvider,
  _lowCreditsProvider,
  _financePendingProvider,
  _financeInsightProvider,
];

export function useDashboardAlerts(restaurantId) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    const results = await Promise.allSettled(
      PROVIDERS.map(fn => fn(null, restaurantId))
    );
    const raw = results
      .filter(r => r.status === "fulfilled" && r.value)
      .map(r => r.value);
    // Note: per-alert 24h dismiss was removed (Feb 2026) because the alerts
    // now render as cards inside the unified InsightRail. Users hide whole
    // categories permanently from the "Editar panel" popover instead —
    // single mechanism, server-synced. The old localStorage entries are
    // auto-cleaned on first load by the migration in DashboardLayout.
    raw.sort((a, b) => {
      const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      if (s !== 0) return s;
      return (b.createdAt || 0) - (a.createdAt || 0);
    });
    setAlerts(raw);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { refresh(); }, [refresh]);

  return { alerts, loading, refresh };
}

export { TONES };
